import React, { useEffect } from 'react';
import '@/App.css';
// Notes module scoped theme (all rules live under .notes-scope — no leak into Inventique).
import '@/notes/notes-theme.css';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AuthProvider, useAuth } from '@/lib/auth';
import { NotesAuthProvider, useNotesAuth } from '@/lib/notesAuth';
import { NotificationsProvider } from '@/lib/notifications';
import { ComposeProvider } from '@/lib/compose';
import { Toaster } from '@/components/ui/sonner';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Notifications from '@/pages/Notifications';
import IdleLogoutGuard from '@/components/IdleLogoutGuard';
import MaintenanceGate, { useMaintenance } from '@/components/MaintenanceGate';
import Diamonds from '@/pages/Diamonds';
import PacketDetail from '@/pages/PacketDetail';
import ColorStones from '@/pages/ColorStones';
import ColorStonePacketDetail from '@/pages/ColorStonePacketDetail';
import Pearls from '@/pages/Pearls';
import PearlPacketDetail from '@/pages/PearlPacketDetail';
import Gold from '@/pages/Gold';
import Vendors from '@/pages/Vendors';
import Customers from '@/pages/Customers';
import Goldsmiths from '@/pages/Goldsmiths';
// Wave 3.59 — Cutter master + Broken Recut Issues list.
import Cutters from '@/pages/Cutters';
import CutterLedger from '@/pages/CutterLedger';
import BrokenPacketIssues from '@/pages/BrokenPacketIssues';
// Wave 4.07 — CAD Developer master + CAD Development report.
import CADDevelopers from '@/pages/CADDevelopers';
import CADReport from '@/pages/CADReport';
import Orders from '@/pages/Orders';
import OrderDetail from '@/pages/OrderDetail';
import ProductionTimeline from '@/pages/ProductionTimeline';
import ReceiveJewellery from '@/pages/ReceiveJewellery';
import Jewellery from '@/pages/Jewellery';
import JewelleryDetail from '@/pages/JewelleryDetail';
import JewelleryTagView from '@/pages/JewelleryTagView';
import { isPublicTagHost } from '@/lib/publicTagHost';
import TagWorkspaceLogin from '@/notes/tagws/TagWorkspaceLogin';
import TagWorkspacePage from '@/notes/tagws/TagWorkspacePage';
import DesignMaster from '@/pages/DesignMaster';
import Reports from '@/pages/Reports';
import UnifiedClosingBalance from '@/pages/UnifiedClosingBalance';
import CustomerGoldLedgerReport from '@/pages/CustomerGoldLedgerReport';
import Logs from '@/pages/Logs';
import AdminCompanies from '@/pages/AdminCompanies';
import AdminUsers from '@/pages/AdminUsers';
import AdminPortalAccess from '@/pages/AdminPortalAccess';
import AdminStorageConsole from '@/pages/AdminStorageConsole';
import AdminDailyDigest from '@/pages/AdminDailyDigest';
import DigestReport from '@/pages/DigestReport';
import EstimatedDeliveryReport from '@/pages/EstimatedDeliveryReport';
import ProductionStageDigest from '@/pages/ProductionStageDigest';
import AccessControlGuard from '@/components/AccessControlGuard';
import Requests from '@/pages/Requests';
import EditRequests from '@/pages/EditRequests';
import VendorDetail from '@/pages/VendorDetail';
import GoldsmithDetail from '@/pages/GoldsmithDetail';
import IssueMemo from '@/pages/IssueMemo';
import VendorReturnMemo from '@/pages/VendorReturnMemo';
import GoldsmithReturnMemo from '@/pages/GoldsmithReturnMemo';
import Memos from '@/pages/Memos';
// Wave 3.99z — Mountings (Semi-Finished Gold Pieces) master + ledger.
import MountingsMaster from '@/pages/MountingsMaster';
import MountingLedger from '@/pages/MountingLedger';
// Wave 3.99h — Portal-wide RBAC gate.
import PermissionGate from '@/components/PermissionGate';
// Premium Notes module (isolated: own auth provider + scoped theme).
import NotesAppShell from '@/notes/NotesAppShell';
import TasksAppShell from '@/notes/TasksAppShell';
import PlannerAppShell from '@/notes/PlannerAppShell';
import ChatLayoutPreview from '@/notes/ChatLayoutPreview';
import ChatBubblePreview from '@/notes/ChatBubblePreview';

// Wave 3.99l — All per-report View sub-keys. The Reports module (and
// individual report pages) open if the user has ANY of these.
const REPORT_VIEW_KEYS = [
  'reports.view',
  'reports.unified_closing.view',
  'reports.memos.view',
  'reports.diamond_closing.view',
  'reports.color_stone_closing.view',
  'reports.pearl_closing.view',
  'reports.gold_ledger.view',
  'reports.jewellery.view',
  'reports.cad.view',
  'reports.production_digest.view',
  'reports.estimated_delivery.view',
];

function Protected() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return (
    <ComposeProvider>
      <Outlet />
    </ComposeProvider>
  );
}

function PublicOnly() {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

// ERP partial-lock gate. While the portal is in PARTIAL (Sales & Approval)
// mode the ERP module is closed: any signed-in ERP user is logged out
// immediately and dropped on the login page (where ERP credentials are then
// rejected as "Invalid credentials"). Notes/Tasks users are unaffected. Wraps
// only the AUTHENTICATED ERP routes; the shared /login page stays reachable.
function ErpPartialLock() {
  const { partial } = useMaintenance();
  const { user, logout } = useAuth();
  useEffect(() => {
    if (partial && user) logout(); // clears session + hard-redirects to /login
  }, [partial, user, logout]);
  // While partial, render nothing — the logout redirect to /login is underway.
  if (partial) return null;
  return <Outlet />;
}

// ---------------------------------------------------------------------------
// Inventique app tree — providers + guards scoped so they NEVER run for the
// isolated Notes module.
// ---------------------------------------------------------------------------
function InventiqueLayout() {
  return (
    <AuthProvider>
      <AccessControlGuard />
      <NotificationsProvider>
        <IdleLogoutGuard />
        <Outlet />
      </NotificationsProvider>
    </AuthProvider>
  );
}

// ---------------------------------------------------------------------------
// Notes module tree — completely separate auth context. A Notes user (or a
// Super Admin driving Notes via the module toggle) resolves here; regular
// Inventique users are rejected by the backend → strict isolation.
// ---------------------------------------------------------------------------
function NotesLayout() {
  return (
    <NotesAuthProvider>
      <Outlet />
    </NotesAuthProvider>
  );
}

function NotesProtected() {
  const { principal, loading, theme } = useNotesAuth();
  if (loading) {
    return (
      <div className={`notes-scope ${theme === 'dark' ? 'dark' : ''} flex h-screen w-full items-center justify-center`} style={{ background: 'var(--n-canvas)' }}>
        <Loader2 className="animate-spin text-[var(--n-accent)]" size={24} />
      </div>
    );
  }
  if (!principal) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function App() {
  // Hide Emergent platform badge - it uses inline !important styles
  React.useEffect(() => {
    const hideBadge = () => {
      const el = document.getElementById('emergent-badge');
      if (el) el.style.setProperty('display', 'none', 'important');
    };
    hideBadge();
    const obs = new MutationObserver(hideBadge);
    obs.observe(document.body, { childList: true, subtree: true });
    const iv = setInterval(hideBadge, 1000);
    return () => { obs.disconnect(); clearInterval(iv); };
  }, []);

  // On the neutral white-label tag domain, show a generic browser-tab title
  // ("Tag Info") instead of the portal's default — keeps the scan experience
  // unbranded. The main portal domain is unaffected.
  React.useEffect(() => {
    if (isPublicTagHost()) {
      try { document.title = 'Tag Info'; } catch (_) { /* noop */ }
    }
  }, []);

  // When served on the neutral white-label tag domain (e.g. rosentiquestag.com)
  // run in TAG-ONLY mode:
  //   • ``/t/:id`` stays PUBLIC (QR scan target, no auth).
  //   • Every other path requires a Notes-auth login (Tag Workspace), NOT the
  //     Inventique ERP login. The portal / ERP routes are NEVER mounted here,
  //     so the management portal can never be reached on the neutral domain.
  if (isPublicTagHost()) {
    return (
      <MaintenanceGate>
        <BrowserRouter>
          <Routes>
            <Route path="/t/:id" element={<JewelleryTagView />} />
            <Route element={<NotesLayout />}>
              <Route path="/login" element={<TagWorkspaceLogin />} />
              <Route element={<NotesProtected />}>
                <Route path="/" element={<TagWorkspacePage />} />
                <Route path="/jewellery" element={<TagWorkspacePage />} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </MaintenanceGate>
    );
  }

  return (
    <MaintenanceGate>
      <BrowserRouter>
        <Routes>
          {/* TEMP — chat layout preview mockup (no auth), remove after selection */}
          <Route path="/chat-preview" element={<ChatLayoutPreview />} />
          <Route path="/chat-bubble-preview" element={<ChatBubblePreview />} />
          {/* Public jewellery tag viewer (QR scan target - no auth, value-free) */}
          <Route path="/t/:id" element={<JewelleryTagView />} />
          {/* ---- Notes module (isolated) ---- */}
          <Route element={<NotesLayout />}>
            <Route path="/notes/login" element={<Navigate to="/login" replace />} />
            <Route element={<NotesProtected />}>
              <Route path="/notes" element={<NotesAppShell />} />
              <Route path="/notes/tasks" element={<TasksAppShell />} />
              <Route path="/notes/planner" element={<PlannerAppShell />} />
            </Route>
            <Route path="/notes/*" element={<Navigate to="/notes" replace />} />
          </Route>

          {/* ---- Inventique ERP ---- */}
          <Route element={<InventiqueLayout />}>
            <Route element={<PublicOnly />}>
              <Route path="/login" element={<Login />} />
            </Route>
            <Route element={<Protected />}>
              <Route element={<ErpPartialLock />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/diamonds" element={<PermissionGate permission="materials.diamonds.see" moduleLabel="Diamonds"><Diamonds /></PermissionGate>} />
              <Route path="/diamonds/packets/:id" element={<PermissionGate permission="materials.diamonds.see" moduleLabel="Diamonds"><PacketDetail /></PermissionGate>} />
              <Route path="/color-stones" element={<PermissionGate permission="materials.color_stones.see" moduleLabel="Colour Stones"><ColorStones /></PermissionGate>} />
              <Route path="/color-stones/packets/:id" element={<PermissionGate permission="materials.color_stones.see" moduleLabel="Colour Stones"><ColorStonePacketDetail /></PermissionGate>} />
              <Route path="/pearls" element={<PermissionGate permission="materials.pearls.see" moduleLabel="Pearls"><Pearls /></PermissionGate>} />
              <Route path="/pearls/packets/:id" element={<PermissionGate permission="materials.pearls.see" moduleLabel="Pearls"><PearlPacketDetail /></PermissionGate>} />
              <Route path="/gold" element={<PermissionGate permission="materials.gold.see" moduleLabel="Gold"><Gold /></PermissionGate>} />
              <Route path="/vendors" element={<PermissionGate permission="vendors.view" moduleLabel="Vendors"><Vendors /></PermissionGate>} />
              <Route path="/vendors/:id" element={<PermissionGate permission="vendors.view" moduleLabel="Vendors"><VendorDetail /></PermissionGate>} />
              <Route path="/customers" element={<PermissionGate permission="orders.view" moduleLabel="Customers" allowSales><Customers /></PermissionGate>} />
              <Route path="/goldsmiths" element={<PermissionGate permission="goldsmiths.view" moduleLabel="Goldsmiths"><Goldsmiths /></PermissionGate>} />
              <Route path="/goldsmiths/:id" element={<PermissionGate permission="goldsmiths.view" moduleLabel="Goldsmiths"><GoldsmithDetail /></PermissionGate>} />
              <Route path="/orders" element={<PermissionGate permission="orders.view" moduleLabel="Order Forms" allowSales><Orders /></PermissionGate>} />
              <Route path="/orders/:id" element={<PermissionGate permission="orders.view" moduleLabel="Order Forms" allowSales><OrderDetail /></PermissionGate>} />
              <Route path="/orders/:id/receive-jewellery" element={<PermissionGate permission="orders.view" moduleLabel="Order Forms"><ReceiveJewellery /></PermissionGate>} />
              <Route path="/production-timeline" element={<PermissionGate permission="orders.view" moduleLabel="Production Timeline" allowSales><ProductionTimeline /></PermissionGate>} />
              <Route path="/jewellery" element={<PermissionGate permission="jewellery.view" moduleLabel="Jewellery"><Jewellery /></PermissionGate>} />
              <Route path="/jewellery/:id" element={<PermissionGate permission="jewellery.view" moduleLabel="Jewellery"><JewelleryDetail /></PermissionGate>} />
              {/* Design Master — visible to ALL roles (no gate; sales get chip-scoping server-side). */}
              <Route path="/designs" element={<DesignMaster />} />
              <Route path="/issue-memos/:id" element={<PermissionGate permission="orders.view" moduleLabel="Memos"><IssueMemo /></PermissionGate>} />
              <Route path="/return-memos/vendor/:memoNo" element={<PermissionGate permission="orders.view" moduleLabel="Memos"><VendorReturnMemo /></PermissionGate>} />
              <Route path="/return-memos/:id" element={<PermissionGate permission="orders.view" moduleLabel="Memos"><GoldsmithReturnMemo /></PermissionGate>} />
              <Route path="/reports" element={<PermissionGate anyOf={REPORT_VIEW_KEYS} moduleLabel="Reports"><Reports /></PermissionGate>} />
              {/* Personal Activity Digest — self-scoped; available to all authenticated users. */}
              <Route path="/reports/digest" element={<DigestReport />} />
              <Route path="/reports/unified-closing-balance" element={<PermissionGate permission="reports.unified_closing.view" moduleLabel="Unified Closing Stock"><UnifiedClosingBalance /></PermissionGate>} />
              <Route path="/reports/memos" element={<PermissionGate permission="reports.memos.view" moduleLabel="Memos" allowSales><Memos /></PermissionGate>} />
              <Route path="/reports/customer-gold-ledger" element={<PermissionGate permission="reports.customer_gold.view" moduleLabel="Customer Gold Report"><CustomerGoldLedgerReport /></PermissionGate>} />
              {/* Wave 4.07 — CAD Development report. */}
              <Route path="/reports/cad-development" element={<PermissionGate permission="reports.cad.view" moduleLabel="CAD Development Report"><CADReport /></PermissionGate>} />
              {/* Production Stage Digest — pivot of active order forms by stage (sales-scoped). */}
              <Route path="/reports/production-stage-digest" element={<PermissionGate permission="reports.production_digest.view" moduleLabel="Production Stage Digest" allowSales><ProductionStageDigest /></PermissionGate>} />
              {/* Estimated Delivery report — active order forms by expected delivery date (sales-scoped). */}
              <Route path="/reports/estimated-delivery" element={<PermissionGate permission="reports.estimated_delivery.view" moduleLabel="Estimated Delivery Report" allowSales><EstimatedDeliveryReport /></PermissionGate>} />
              <Route path="/requests" element={<PermissionGate permission="requests.view" moduleLabel="Delete Requests"><Requests /></PermissionGate>} />
              <Route path="/edit-requests" element={<PermissionGate permission="requests.view" moduleLabel="Edit Requests"><EditRequests /></PermissionGate>} />
              <Route path="/logs" element={<PermissionGate permission="logs.view" moduleLabel="Activity Logs"><Logs /></PermissionGate>} />
              <Route path="/admin/companies" element={<AdminCompanies />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/portal-access" element={<AdminPortalAccess />} />
              <Route path="/admin/storage" element={<AdminStorageConsole />} />
              <Route path="/admin/daily-digest" element={<AdminDailyDigest />} />
              {/* Wave 3.59 — Cutter master + Broken Recut Issues list. */}
              <Route path="/cutters" element={<PermissionGate permission="cutters.view" moduleLabel="Cutters"><Cutters /></PermissionGate>} />
              <Route path="/cutters/ledger" element={<PermissionGate permission="cutters.view" moduleLabel="Cutter Ledger"><CutterLedger /></PermissionGate>} />
              {/* Wave 4.07 — CAD Developers master. */}
              <Route path="/cad-developers" element={<PermissionGate permission="cad_developers.view" moduleLabel="CAD Developers"><CADDevelopers /></PermissionGate>} />
              <Route path="/broken-packet-issues" element={<PermissionGate anyOf={['materials.diamonds.see', 'materials.color_stones.see', 'materials.pearls.see']} moduleLabel="Broken Recut Issues"><BrokenPacketIssues /></PermissionGate>} />
              {/* Wave 3.99z — Mountings (Semi-Finished Gold Pieces) master + per-item ledger route. */}
              <Route path="/mountings" element={<PermissionGate permission="materials.mountings.see" moduleLabel="Mountings"><MountingsMaster /></PermissionGate>} />
              <Route path="/mountings/:id/ledger" element={<PermissionGate permission="materials.mountings.see" moduleLabel="Mountings"><MountingLedger /></PermissionGate>} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </MaintenanceGate>
  );
}

export default App;
